import React, { useCallback, useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiLock, FiCheck, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LearningPathCompletion } from './learning-path-completion';
import { PATHS } from '@/routes/paths';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScormQuizCards } from '@/pages/study-guide/study-guide-block/flashcard-quiz/scorm-quiz-cards';
import { learningPathEndpoints } from './block-endpoints';
import { StreakPanel } from './streak-brain';
import { TypewriterText } from '@/components/custom/typewriter-text';
import tiaAvatar from '@/components/custom/tia/img/tia-avatar.png';
import tiaTrilhaJornada from '@/assets/images/tia/TIA-TRILHA-JORNADA.png';
import flagIcon from '@/pages/study-guide/study-guide-timeline/img/flag.svg';
import trophyIcon from '@/pages/study-guide/study-guide-timeline/img/trophy.svg';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const statusLabel = ({ done, skipped, isCurrent, open }) => {
  if (done) return 'Concluído';
  if (skipped) return 'Pulado';
  if (isCurrent) return 'Você está aqui 🎯';
  return open ? 'Disponível' : 'Bloqueado';
};

// Mensagem de incentivo da T.I.A conforme o estado da trilha.
const encourage = ({ mastered, streakCount, walked }) => {
  if (mastered >= 100) return 'Você dominou a trilha! Que orgulho. 🎉';
  if (streakCount >= 5) return `Você está há ${streakCount} dias seguidos. Imparável! 🔥`;
  if (streakCount >= 1) return `${streakCount} ${streakCount === 1 ? 'dia' : 'dias'} de ofensiva! Continue firme. 💪`;
  if (walked > 0) return 'Bom ritmo! Cada bloco te deixa mais perto. Bora continuar?';
  return 'Bora começar? Vou te acompanhar em cada bloco da sua trilha. 💙';
};

export function LearningPathView() {
  const client = useClient();
  const navigate = useNavigate();
  const { userPathId } = useParams();
  const [trilha, setTrilha] = useState(null);
  const [loading, setLoading] = useState(true);
  // Checkpoint de revisão abre num modal SOBRE a trilha (não navega): null | 'loading' | { blockId, sectionId, cards }
  const [reviewState, setReviewState] = useState(null);

  const fetchCurrent = useCallback(async () => {
    const endpoint = userPathId ? `user_learning_paths/${userPathId}` : 'user_learning_paths/current';
    const response = await client.fetch(endpoint);
    setTrilha(response.data || null);
    setLoading(false);
  }, [client, userPathId]);

  useEffect(() => {
    fetchCurrent();
  }, [fetchCurrent]);

  if (loading) return <Loader />;
  if (!trilha) return navigate(PATHS.LEARNING_PATH.ROOT);
  if (trilha.status === 'completed') {
    return <LearningPathCompletion onGenerateNew={() => navigate(PATHS.LEARNING_PATH.ROOT)} />;
  }

  const blocks = trilha['blocks'] || [];
  const currentIndex = blocks.findIndex(b => b.state === 'pending');
  const walked = trilha['percent-walked'] ?? 0;
  const mastered = trilha['percent-mastered'] ?? 0;
  const streakCount = trilha['streak']?.['current-count'] ?? 0;
  const tiaMessage = encourage({ mastered, streakCount, walked });
  const playful = usePlayfulStudent();

  // Unlock rule (spec §6): done/skipped blocks are always open;
  // the first pending block is the "current" (open); all subsequent pending blocks are locked.
  const isOpen = index => {
    const block = blocks[index];
    if (block.state === 'done' || block.state === 'skipped') return true;
    if (currentIndex === -1) return true; // no pending blocks — all open
    return index === currentIndex;
  };

  // Bloco de revisão abre o quiz num modal aqui mesmo; bloco de conteúdo navega pro player.
  const handleBlockClick = block => {
    if (block.kind === 'review') openReview(block);
    else navigate(PATHS.LEARNING_PATH.BLOCK(block.id));
  };

  const openReview = async block => {
    setReviewState('loading');
    const res = await client.fetch(learningPathEndpoints(block.id).sections);
    const section = (res.data || []).find(s => s.scorm?.scorm_type === 'quiz_cards');
    if (!section) return setReviewState(null);
    setReviewState({ blockId: block.id, sectionId: section.id, cards: section.scorm.content?.cards || [] });
  };

  const markReviewSectionDone = sectionId => client.mutate(learningPathEndpoints(reviewState.blockId).sectionDone(sectionId), { done: true }, { method: 'put' });

  const finishReview = async () => {
    setReviewState(null);
    setLoading(true);
    await fetchCurrent();
  };

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      { route: PATHS.LEARNING_PATH.ROOT, name: 'Trilha', isActive: false },
      {
        route: trilha?.id ? PATHS.LEARNING_PATH.CURRENT_BY_ID(trilha.id) : PATHS.LEARNING_PATH.CURRENT,
        name: trilha['learning-path-name'] || 'Minha trilha',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      <div className={playful ? 'w-full' : 'mx-auto w-full max-w-5xl'}>
        <div className={cn('flex w-full flex-col gap-8 lg:flex-row lg:items-start', playful && 'mt-24')}>
          <div className="min-w-0 flex-1">
            {playful && (
              // T.I.A grande estourando o card (padrão das telas de Conquistas/Trilha).
              // Fica DENTRO da coluna (flex-1) para o card de streak alinhar com o topo
              // dela e o banner ficar mais estreito (o aside de 320px ocupa a direita).
              <div
                className="relative mb-8 flex items-center rounded-[24px] p-4 pl-[150px] shadow-card"
                style={{ background: 'var(--color-bg-lilac)' }}
              >
                <img
                  src={tiaTrilhaJornada}
                  alt="T.I.A"
                  className="pointer-events-none absolute bottom-0 left-2 z-10 h-[180px] w-auto object-contain object-bottom"
                />
                <div className="relative z-10 max-w-[800px] rounded-[16px] rounded-bl-none bg-white p-4 shadow-card">
                  <span
                    className="block text-xs font-extrabold"
                    style={{ color: 'var(--color-secondary-dark)' }}
                  >
                    T.I.A
                  </span>
                  <p
                    className="mb-0 text-sm font-bold leading-relaxed"
                    style={{ color: '#222731' }}
                  >
                    <TypewriterText text={tiaMessage} />
                  </p>
                </div>
              </div>
            )}
            {!playful && (
              <div className="mb-7 flex items-start gap-3">
                <img
                  src={tiaAvatar}
                  alt="T.I.A"
                  className="h-12 w-12 shrink-0 rounded-full border-2 border-white object-cover shadow-card"
                />
                <div className="max-w-sm rounded-[4px] rounded-tl-none bg-white p-3 shadow-card">
                  <span
                    className="block text-[10px] font-extrabold"
                    style={{ color: 'var(--color-secondary-dark)' }}
                  >
                    T.I.A
                  </span>
                  <p
                    className="text-xs font-light leading-relaxed"
                    style={{ color: '#222731' }}
                  >
                    {tiaMessage}
                  </p>
                </div>
              </div>
            )}

            {blocks.length === 0 ? (
              <div
                style={{ animation: `trilha-in 380ms ${EASE_OUT} both` }}
                className="rounded-2xl border border-neutral-100 bg-white p-10 text-center shadow-card"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-done text-success">
                  <FiCheckCircle className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900">Você está em dia! 🎯</h3>
                <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-neutral-500">Não há conteúdo novo na sua trilha agora. Gere uma nova sempre que quiser revisar suas lacunas.</p>
                <Button
                  variant="primary"
                  className="mt-6"
                  onClick={() => navigate(PATHS.LEARNING_PATH.ROOT)}
                >
                  Voltar ao início
                </Button>
              </div>
            ) : (
              <>
                {/* Largada — bandeira quadriculada do legado */}
                <div className={cn('mb-4 flex items-center gap-4', playful && 'rounded-[20px] border border-neutral-100 bg-white p-4 shadow-card')}>
                  <span className="flex w-12 shrink-0 justify-center">
                    <img
                      src={flagIcon}
                      alt=""
                      className="h-10 w-10"
                    />
                  </span>
                  <span className={cn('text-sm font-bold', playful ? 'text-mission' : 'text-primary-dark')}>Largada! Sua jornada começa aqui.</span>
                </div>

                <ol className="relative">
                  {/* Caminho curvo (desktop): liga o centro de cada nó ao próximo em zigzag.
                    Linhas com altura fixa (160px) → posições previsíveis. Nó par x=132, ímpar x=32. */}
                  {playful && blocks.length > 1 && (
                    <svg
                      aria-hidden="true"
                      width="220"
                      height={(blocks.length - 1) * 160 + 80}
                      className="pointer-events-none absolute left-0 top-0 z-0 hidden lg:block"
                    >
                      {blocks.slice(0, -1).map((block, i) => {
                        const x0 = i % 2 === 0 ? 140 : 40;
                        const x1 = (i + 1) % 2 === 0 ? 140 : 40;
                        const y0 = i * 160 + 40;
                        const y1 = (i + 1) * 160 + 40;
                        const midY = (y0 + y1) / 2;
                        return (
                          <path
                            key={block.id}
                            d={`M ${x0} ${y0} C ${x0} ${midY}, ${x1} ${midY}, ${x1} ${y1}`}
                            fill="none"
                            stroke={block.state === 'done' ? '#27AE60' : '#CFC7E6'}
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray="9 11"
                          />
                        );
                      })}
                    </svg>
                  )}
                  {blocks.map((block, index) => {
                    const open = isOpen(index);
                    const isCurrent = index === currentIndex;
                    const done = block.state === 'done';
                    const skipped = block.state === 'skipped';
                    const last = index === blocks.length - 1;
                    const isReview = block.kind === 'review';
                    // Relevo 3D/chanfrado do nó (lip colorido por estado) — só no tema lúdico.
                    const nodeLip = !open
                      ? '0 3px 0 #CFC9E0'
                      : done
                        ? '0 4px 0 #1E8049, 0 5px 10px rgba(0,0,0,0.16)'
                        : isCurrent
                          ? '0 4px 0 color-mix(in srgb, var(--color-mission) 58%, #000), 0 5px 10px rgba(0,0,0,0.18)'
                          : '0 3px 0 #DCD7EA, 0 4px 8px rgba(0,0,0,0.06)';
                    // Matéria (taxonomia pai do descritor) + tópico sem o código "Dx ·".
                    const materia = block['taxonomy-parent-name'];
                    // Remove o código de descritor no início (ex.: "D12 · ", "2P4.1 · ",
                    // "2AL1.3 · "): token alfanumérico com dígito seguido de separador.
                    const topic = (block['video-title'] || '').replace(/^(?=[A-Za-z0-9.]*\d)[A-Za-z0-9.]+\s*[·:—–]\s*/, '') || block['taxonomy-name'] || `Bloco ${block.position + 1}`;

                    return (
                      <li
                        key={block.id}
                        style={{ animation: `trilha-in 360ms ${EASE_OUT} ${index * 60}ms both` }}
                        className={cn('relative flex gap-4 pb-3 last:pb-0', playful && 'items-start pb-0', playful && !last && 'lg:min-h-[160px]', playful && index % 2 === 0 && 'lg:pl-[100px]', playful && index % 2 === 1 && 'lg:pr-[100px]')}
                      >
                        {/* rail: node + connector */}
                        <div className="relative flex flex-col items-center">
                          <span
                            className={cn(
                              'z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 text-base font-extrabold transition-colors duration-150 ease-out-strong',
                              playful && 'h-20 w-20 text-2xl',
                              done && 'border-success bg-success text-white',
                              isCurrent && 'border-mission bg-mission text-white',
                              isCurrent && !playful && 'animate-[mission-pulse_3s_ease-in-out_infinite] ring-4 ring-mission-light',
                              !done && !isCurrent && open && 'border-neutral-200 bg-white text-neutral-500',
                              !open && 'border-neutral-100 bg-neutral-50 text-neutral-300'
                            )}
                            style={playful ? { boxShadow: nodeLip } : undefined}
                          >
                            {done ? <FiCheck className="h-5 w-5" /> : !open ? <FiLock className="h-4 w-4" /> : index + 1}
                          </span>
                          {!last &&
                            (playful ? (
                              // Mobile: linha reta simples (no desktop o caminho curvo é o overlay SVG)
                              <span className={cn('w-1 flex-1 rounded-full lg:hidden', done ? 'bg-[#27AE60]' : 'bg-[#E6E1F2]')} />
                            ) : (
                              <span className={cn('w-1 flex-1 rounded-full', done ? 'bg-success' : 'bg-neutral-200')} />
                            ))}
                        </div>

                        {/* card */}
                        <button
                          type="button"
                          data-testid={`block-${block.id}`}
                          disabled={!open}
                          onClick={() => open && handleBlockClick(block)}
                          className={cn(
                            'mb-1 flex min-w-0 flex-1 items-center justify-between gap-3 rounded-2xl border bg-white px-5 py-4 text-left',
                            playful && 'py-6',
                            'transition-[box-shadow,transform,border-color] duration-150 ease-out-strong',
                            open ? 'cursor-pointer shadow-card hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.99]' : 'cursor-default opacity-70',
                            isCurrent ? 'border-mission/60 bg-mission-light/30' : 'border-neutral-100'
                          )}
                        >
                          <span className="min-w-0">
                            <span className="mb-1.5 flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">{statusLabel({ done, skipped, isCurrent, open })}</span>
                              {!isReview && materia && (
                                <Badge
                                  variant="bright"
                                  size="xs"
                                >
                                  {materia}
                                </Badge>
                              )}
                            </span>
                            <span className="block truncate text-[15px] font-bold text-neutral-800">{isReview ? 'Revisão 🧠' : topic}</span>
                          </span>
                          {open && !done && <FiArrowRight className={cn('h-5 w-5 shrink-0', isCurrent ? 'text-mission' : 'text-neutral-300')} />}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}

            {blocks.length > 0 && (
              <div className={cn('mt-2 flex items-center gap-4', playful && 'rounded-[20px] border border-neutral-100 bg-white p-4 shadow-card')}>
                <span className="flex w-12 shrink-0 justify-center">
                  <img
                    src={trophyIcon}
                    alt=""
                    className="h-10 w-10"
                  />
                </span>
                <span className={cn('text-sm font-bold', playful ? 'text-mission' : 'text-primary-dark')}>Linha de chegada: complete tudo e a conquista é sua!</span>
              </div>
            )}
          </div>

          {/* Painel do streak (cérebro) — resgatado do legado: gradiente + nível, sticky à direita */}
          <aside className="w-full lg:sticky lg:top-[112px] lg:w-[320px] lg:shrink-0">
            <StreakPanel
              streak={trilha['streak']}
              walked={walked}
              mastered={mastered}
              onContinue={currentIndex !== -1 ? () => handleBlockClick(blocks[currentIndex]) : undefined}
            />
          </aside>
        </div>
      </div>

      {/* Checkpoint de revisão: quiz em cartas num modal SOBRE a trilha (não navega pro player). */}
      <Dialog
        open={!!reviewState}
        onOpenChange={o => {
          if (!o) setReviewState(null);
        }}
      >
        <DialogContent className="sm:max-w-[680px]">
          <DialogTitle className="text-lg font-bold text-neutral-800">Revisão 🧠 — Checkpoint</DialogTitle>
          {reviewState === 'loading' ? (
            <div className="py-12">
              <Loader />
            </div>
          ) : reviewState ? (
            <div className="pt-2">
              <ScormQuizCards
                cards={reviewState.cards}
                scorm={{ id: reviewState.sectionId }}
                setScormSectionDone={markReviewSectionDone}
                onComplete={finishReview}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
